/**
 * Hotel/Portal Client Create/Edit Form - YUL ON TAP
 * 
 * OS-Style Pattern: Same form structure as Restaurant form.
 * Step 1 of 4-step onboarding wizard.
 * 
 * Supports two business types:
 * - Hotel: Traditional hotel/lodging properties
 * - Portal Client: Non-hotel businesses (restaurants, retail, etc.)
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { hotelsAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { toast } from 'sonner';
import { Building2, Store, ArrowLeft, Loader2, Save } from 'lucide-react';
import brandConfig, { BRAND, INDUSTRY_TYPES } from '../../config/brand';

export default function HotelForm() {
  const { id } = useParams();
  const isEditing = !!id;
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    hotel_code: '',
    business_type: 'hotel', // 'hotel' or 'portal_client'
    address: '',
    city: '',
    contact_email: '',
    contact_phone: '',
    manager_email: '',
    star_rating: null,
  });

  useEffect(() => {
    if (isEditing) {
      fetchHotel();
    }
  }, [id]);

  const fetchHotel = async () => {
    setLoading(true);
    try {
      const response = await hotelsAPI.get(id);
      const hotel = response.data;
      setFormData({
        name: hotel.name || '',
        hotel_code: hotel.hotel_code || '',
        business_type: hotel.business_type || 'hotel',
        address: hotel.address || '',
        city: hotel.city || '',
        contact_email: hotel.contact_email || '',
        contact_phone: hotel.contact_phone || '',
        manager_email: hotel.manager_email || '',
        star_rating: hotel.star_rating,
      });
    } catch (error) {
      toast.error('Failed to load property');
      navigate('/hotels');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const data = {
        ...formData,
        star_rating: formData.business_type === 'hotel' && formData.star_rating ? parseInt(formData.star_rating) : null,
      };

      if (isEditing) {
        await hotelsAPI.update(id, data);
        toast.success('Property updated successfully');
        navigate(`/hotels/${id}`);
      } else {
        const response = await hotelsAPI.create(data);
        toast.success(`${formData.business_type === 'hotel' ? 'Hotel' : 'Portal Client'} created successfully`);
        // Navigate to room setup (Step 2 of onboarding)
        navigate(`/hotels/${response.data.id}/rooms`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save property');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(224,71%,4%)] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(224,71%,4%)]">
      {/* Header */}
      <header className="border-b border-[hsl(217,19%,27%)] px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center space-x-4">
          <button
            onClick={() => navigate(isEditing ? `/hotels/${id}` : '/hotels')}
            data-testid="back-btn"
            className="flex items-center space-x-2 text-[hsl(215,20%,65%)] hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back</span>
          </button>
          <div className="h-6 w-px bg-[hsl(217,19%,27%)]" />
          <div className="flex items-center space-x-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
              formData.business_type === 'portal_client' ? 'bg-purple-500/20' : 'bg-blue-500/20'
            }`}>
              {formData.business_type === 'portal_client' ? (
                <Store className="w-5 h-5 text-purple-500" />
              ) : (
                <Building2 className="w-5 h-5 text-blue-500" />
              )}
            </div>
            <span className="text-lg font-semibold text-white">
              {isEditing 
                ? `Edit ${brandConfig.terms.businessName}` 
                : brandConfig.terms.addProperty}
            </span>
          </div>
        </div>
      </header>

      {/* Form */}
      <main className="max-w-3xl mx-auto px-6 py-8">
        {/* Progress Indicator (for new properties) */}
        {!isEditing && (
          <div className="mb-8">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-blue-500 font-medium">Step 1: Client Details</span>
              <span className="text-[hsl(215,20%,65%)]">1 of 4</span>
            </div>
            <div className="h-2 bg-[hsl(217,19%,17%)] rounded-full overflow-hidden">
              <div className="h-full w-1/4 bg-blue-500 rounded-full" />
            </div>
          </div>
        )}

        <Card className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,27%)]">
          <CardHeader>
            <CardTitle className="text-xl text-white">
              {isEditing ? `Edit ${brandConfig.terms.propertyInfoTitle}` : brandConfig.terms.propertyInfoTitle}
            </CardTitle>
            <CardDescription className="text-[hsl(215,20%,65%)]">
              {isEditing
                ? brandConfig.terms.propertyInfoEditDesc
                : brandConfig.terms.propertyInfoDesc}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Business Type / Industry Type Selection (only for new properties) */}
              {!isEditing && (
                <div className="space-y-3">
                  <Label className="text-[hsl(210,40%,98%)]">{brandConfig.terms.propertyTypeLabel} *</Label>
                  {BRAND === 'tapelligence' ? (
                    <Select
                      value={formData.business_type}
                      onValueChange={(value) => handleChange('business_type', value)}
                      data-testid="industry-type-select"
                    >
                      <SelectTrigger className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white">
                        <SelectValue placeholder="Select industry type" />
                      </SelectTrigger>
                      <SelectContent>
                        {INDUSTRY_TYPES.map(({ value, label }) => (
                          <SelectItem key={value} value={value}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <RadioGroup
                      value={formData.business_type}
                      onValueChange={(value) => handleChange('business_type', value)}
                      className="grid grid-cols-2 gap-4"
                      data-testid="business-type-radio"
                    >
                      <div className={`flex items-center space-x-3 p-4 rounded-lg border cursor-pointer transition-colors ${
                        formData.business_type === 'hotel'
                          ? 'border-blue-500 bg-blue-600/10'
                          : 'border-[hsl(217,19%,27%)] hover:border-[hsl(217,19%,40%)]'
                      }`}>
                        <RadioGroupItem value="hotel" id="type-hotel" />
                        <label htmlFor="type-hotel" className="flex-1 cursor-pointer">
                          <div className="flex items-center space-x-2">
                            <Building2 className="w-5 h-5 text-blue-400" />
                            <span className="text-white font-medium">Hotel</span>
                          </div>
                          <p className="text-xs text-[hsl(215,20%,65%)] mt-1">
                            Hotels, motels, resorts, B&Bs
                          </p>
                        </label>
                      </div>

                      <div className={`flex items-center space-x-3 p-4 rounded-lg border cursor-pointer transition-colors ${
                        formData.business_type === 'portal_client'
                          ? 'border-purple-500 bg-purple-600/10'
                          : 'border-[hsl(217,19%,27%)] hover:border-[hsl(217,19%,40%)]'
                      }`}>
                        <RadioGroupItem value="portal_client" id="type-portal" />
                        <label htmlFor="type-portal" className="flex-1 cursor-pointer">
                          <div className="flex items-center space-x-2">
                            <Store className="w-5 h-5 text-purple-400" />
                            <span className="text-white font-medium">Portal Client</span>
                          </div>
                          <p className="text-xs text-[hsl(215,20%,65%)] mt-1">
                            Restaurants, retail, offices, other
                          </p>
                        </label>
                      </div>
                    </RadioGroup>
                  )}
                </div>
              )}

              {/* Business Name */}
              <div className="space-y-2">
                <Label htmlFor="name" className="text-[hsl(210,40%,98%)]">
                  {brandConfig.terms.businessName} *
                </Label>
                <Input
                  id="name"
                  data-testid="hotel-name-input"
                  type="text"
                  placeholder={brandConfig.terms.businessNamePlaceholder}
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  required
                  className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white placeholder:text-[hsl(215,20%,45%)]"
                />
              </div>

              {/* Business Code - Used as prefix for Card/Tag IDs */}
              <div className="space-y-2">
                <Label htmlFor="hotel_code" className="text-[hsl(210,40%,98%)]">
                  {brandConfig.terms.businessCode} *
                </Label>
                <Input
                  id="hotel_code"
                  data-testid="hotel-code-input"
                  type="text"
                  placeholder={brandConfig.terms.businessCodePlaceholder}
                  value={formData.hotel_code}
                  onChange={(e) => handleChange('hotel_code', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                  required
                  maxLength={12}
                  className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white placeholder:text-[hsl(215,20%,45%)] uppercase"
                />
                <p className="text-xs text-[hsl(215,20%,65%)]">
                  {brandConfig.terms.businessCodeDesc}
                </p>
              </div>

              {/* Address */}
              <div className="space-y-2">
                <Label htmlFor="address" className="text-[hsl(210,40%,98%)]">
                  Address
                </Label>
                <Input
                  id="address"
                  data-testid="hotel-address-input"
                  type="text"
                  placeholder="123 Main Street"
                  value={formData.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white placeholder:text-[hsl(215,20%,45%)]"
                />
              </div>

              {/* City */}
              <div className="space-y-2">
                <Label htmlFor="city" className="text-[hsl(210,40%,98%)]">
                  City
                </Label>
                <Input
                  id="city"
                  data-testid="hotel-city-input"
                  type="text"
                  placeholder="Montreal"
                  value={formData.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white placeholder:text-[hsl(215,20%,45%)]"
                />
              </div>

              {/* Contact Email */}
              <div className="space-y-2">
                <Label htmlFor="contact_email" className="text-[hsl(210,40%,98%)]">
                  Contact Email
                </Label>
                <Input
                  id="contact_email"
                  data-testid="hotel-email-input"
                  type="email"
                  placeholder="contact@business.com"
                  value={formData.contact_email}
                  onChange={(e) => handleChange('contact_email', e.target.value)}
                  className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white placeholder:text-[hsl(215,20%,45%)]"
                />
              </div>

              {/* Manager Email (for low-rating notifications) */}
              <div className="space-y-2">
                <Label htmlFor="manager_email" className="text-[hsl(210,40%,98%)]">
                  Manager Email
                  <span className="text-xs text-[hsl(215,20%,65%)] ml-2">(for low-rating alerts)</span>
                </Label>
                <Input
                  id="manager_email"
                  data-testid="hotel-manager-email-input"
                  type="email"
                  placeholder="manager@business.com"
                  value={formData.manager_email}
                  onChange={(e) => handleChange('manager_email', e.target.value)}
                  className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white placeholder:text-[hsl(215,20%,45%)]"
                />
                <p className="text-xs text-[hsl(215,20%,65%)]">
                  This email will receive notifications when guests submit surveys with 3 stars or less.
                </p>
              </div>

              {/* Contact Phone */}
              <div className="space-y-2">
                <Label htmlFor="contact_phone" className="text-[hsl(210,40%,98%)]">
                  Contact Phone
                </Label>
                <Input
                  id="contact_phone"
                  data-testid="hotel-phone-input"
                  type="tel"
                  placeholder="+1 (555) 123-4567"
                  value={formData.contact_phone}
                  onChange={(e) => handleChange('contact_phone', e.target.value)}
                  className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white placeholder:text-[hsl(215,20%,45%)]"
                />
              </div>

              {/* Star Rating (Only for Hotels) */}
              {formData.business_type === 'hotel' && (
                <div className="space-y-2">
                  <Label htmlFor="star_rating" className="text-[hsl(210,40%,98%)]">
                    Star Rating
                  </Label>
                  <Select
                    value={formData.star_rating?.toString() || ''}
                    onValueChange={(value) => handleChange('star_rating', value)}
                  >
                    <SelectTrigger
                      data-testid="hotel-rating-select"
                      className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white"
                    >
                      <SelectValue placeholder="Select rating" />
                    </SelectTrigger>
                    <SelectContent className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,27%)] z-50">
                      {[1, 2, 3, 4, 5].map((rating) => (
                        <SelectItem
                          key={rating}
                          value={rating.toString()}
                          className="text-white hover:bg-[hsl(217,19%,17%)] focus:bg-[hsl(217,19%,17%)] cursor-pointer"
                        >
                          {'★'.repeat(rating)} {rating} Star{rating > 1 ? 's' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Submit Button */}
              <div className="flex items-center justify-end space-x-4 pt-4">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => navigate(isEditing ? `/hotels/${id}` : '/hotels')}
                  className="text-[hsl(215,20%,65%)] hover:text-white"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  data-testid="save-hotel-btn"
                  className={`text-white ${
                    formData.business_type === 'portal_client' 
                      ? 'bg-purple-600 hover:bg-purple-700' 
                      : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      {isEditing ? 'Save Changes' : 'Continue to Locations'}
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
